import { useState, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  ShieldCheck,
  Users,
  FileText,
  TestTube2,
  BarChart3,
  Import,
  Loader2,
  Plus,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { SearchInput } from "@/components/ui/search-input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { platformApi, RBACPolicy, RBACRole } from "@/services/api/platform";
import { usePaginatedData, PaginatedResponse } from "@/hooks/usePaginatedData";

export const Route = createFileRoute("/portal/platform/rbac")({
  component: RBACPage,
});

function RBACPage() {
  const { token } = useAuth();
  const { can } = usePermissions();
  const canViewRbac = can("view", "rbac");
  const [activeTab, setActiveTab] = useState("policies");
  const [policySearch, setPolicySearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [policySortBy, setPolicySortBy] = useState("subject");
  const [policySortOrder, setPolicySortOrder] = useState<"asc" | "desc">("asc");
  const [roleSortBy, setRoleSortBy] = useState("name");
  const [roleSortOrder, setRoleSortOrder] = useState<"asc" | "desc">("asc");

  // Server-side pagination for policies
  const fetchPolicies = useCallback(
    async (page: number, pageSize: number): Promise<PaginatedResponse<RBACPolicy>> => {
      if (!token) return { data: [], totalCount: 0, page, pageSize };
      try {
        const response = await platformApi.listPolicies(token, {
          offset: (page - 1) * pageSize,
          limit: pageSize,
          search: policySearch || undefined,
          sort_by: policySortBy,
          sort_order: policySortOrder,
        });
        return {
          data: response.items || [],
          totalCount: response.total,
          page,
          pageSize,
        };
      } catch (error) {
        console.error("Failed to load policies:", error);
        toast.error("Failed to load policies");
        return { data: [], totalCount: 0, page, pageSize };
      }
    },
    [token, policySearch, policySortBy, policySortOrder],
  );

  const {
    data: policies,
    isLoading: policiesLoading,
    pagination: policiesPagination,
    goToPage: policiesGoToPage,
    nextPage: policiesNextPage,
    prevPage: policiesPrevPage,
    firstPage: policiesFirstPage,
    lastPage: policiesLastPage,
    setPageSize: policiesSetPageSize,
    refresh: refreshPolicies,
    canGoNext: policiesCanGoNext,
    canGoPrev: policiesCanGoPrev,
    startIndex: policiesStartIndex,
    endIndex: policiesEndIndex,
  } = usePaginatedData<RBACPolicy>({
    fetchFn: fetchPolicies,
    initialPageSize: 25,
    dependencies: [token, policySearch, policySortBy, policySortOrder],
  });

  function handlePolicySort(column: string) {
    if (policySortBy === column) {
      setPolicySortOrder(policySortOrder === "asc" ? "desc" : "asc");
    } else {
      setPolicySortBy(column);
      setPolicySortOrder("asc");
    }
  }
  function getPolicySortDirection(column: string): "asc" | "desc" | null {
    return policySortBy === column ? policySortOrder : null;
  }

  // Server-side pagination for roles
  const fetchRoles = useCallback(
    async (page: number, pageSize: number): Promise<PaginatedResponse<RBACRole>> => {
      if (!token) return { data: [], totalCount: 0, page, pageSize };
      try {
        const response = await platformApi.listRoles(token, {
          offset: (page - 1) * pageSize,
          limit: pageSize,
          search: roleSearch || undefined,
          sort_by: roleSortBy,
          sort_order: roleSortOrder,
        });
        return {
          data: response.items || [],
          totalCount: response.total,
          page,
          pageSize,
        };
      } catch (error) {
        console.error("Failed to load roles:", error);
        toast.error("Failed to load roles");
        return { data: [], totalCount: 0, page, pageSize };
      }
    },
    [token, roleSearch, roleSortBy, roleSortOrder],
  );

  const {
    data: roles,
    isLoading: rolesLoading,
    pagination: rolesPagination,
    goToPage: rolesGoToPage,
    nextPage: rolesNextPage,
    prevPage: rolesPrevPage,
    firstPage: rolesFirstPage,
    lastPage: rolesLastPage,
    setPageSize: rolesSetPageSize,
    refresh: refreshRoles,
    canGoNext: rolesCanGoNext,
    canGoPrev: rolesCanGoPrev,
    startIndex: rolesStartIndex,
    endIndex: rolesEndIndex,
  } = usePaginatedData<RBACRole>({
    fetchFn: fetchRoles,
    initialPageSize: 25,
    dependencies: [token, roleSearch, roleSortBy, roleSortOrder],
  });

  function handleRoleSort(column: string) {
    if (roleSortBy === column) {
      setRoleSortOrder(roleSortOrder === "asc" ? "desc" : "asc");
    } else {
      setRoleSortBy(column);
      setRoleSortOrder("asc");
    }
  }
  function getRoleSortDirection(column: string): "asc" | "desc" | null {
    return roleSortBy === column ? roleSortOrder : null;
  }

  if (!canViewRbac) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Role-Based Access Control</h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage policies, roles, and permissions across the platform
          </p>
        </div>
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-center py-12 text-muted-foreground">
            <ShieldCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>You don't have permission to view access control settings</p>
            <p className="text-xs mt-2">Contact your organisation administrator for access</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Role-Based Access Control</h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage policies, roles, and permissions across the platform
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            refreshPolicies();
            refreshRoles();
          }}
          className="rounded-full"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Content Panel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="policies" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Policies
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Roles
            </TabsTrigger>
            <TabsTrigger value="tester" className="flex items-center gap-2">
              <TestTube2 className="h-4 w-4" />
              Policy Tester
            </TabsTrigger>
            <TabsTrigger value="statistics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Statistics
            </TabsTrigger>
            <TabsTrigger
              value="import-export"
              className="flex items-center gap-2"
            >
              <Import className="h-4 w-4" />
              Import/Export
            </TabsTrigger>
          </TabsList>

          <TabsContent value="policies" className="mt-6 space-y-6">
            {/* Controls bar */}
            <div className="flex flex-wrap items-center gap-3">
              <SearchInput
                value={policySearch}
                onChange={(e) => setPolicySearch(e.target.value)}
                onClear={() => setPolicySearch("")}
                placeholder="Search by subject, resource, or action..."
                containerClassName="flex-1 min-w-[200px]"
              />
              <Button size="sm" disabled>
                <Plus className="mr-2 h-4 w-4" />
                Add Policy
              </Button>
            </div>
            {/* Table */}
            {policiesLoading && policies.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : policies.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No policies found</h3>
                <p className="text-muted-foreground">
                  {policySearch ? "Try a different search term." : "No access control policies have been configured."}
                </p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead
                        direction={getPolicySortDirection("subject")}
                        onSort={() => handlePolicySort("subject")}
                      >
                        Subject
                      </SortableTableHead>
                      <SortableTableHead
                        direction={getPolicySortDirection("organisation")}
                        onSort={() => handlePolicySort("organisation")}
                      >
                        Organisation
                      </SortableTableHead>
                      <SortableTableHead
                        direction={getPolicySortDirection("resource")}
                        onSort={() => handlePolicySort("resource")}
                      >
                        Resource
                      </SortableTableHead>
                      <SortableTableHead
                        direction={getPolicySortDirection("action")}
                        onSort={() => handlePolicySort("action")}
                      >
                        Action
                      </SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {policies.map((policy, index) => (
                      <TableRow
                        key={`${policy.subject}-${policy.resource}-${index}`}
                      >
                        <TableCell className="font-mono text-sm">
                          {policy.subject}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {policy.organisation}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {policy.resource}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {policy.action}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {policiesPagination.totalCount > 0 && (
                  <div className="mt-4 border-t pt-4">
                    <Pagination
                      page={policiesPagination.page}
                      totalPages={policiesPagination.totalPages}
                      totalCount={policiesPagination.totalCount}
                      pageSize={policiesPagination.pageSize}
                      pageSizeOptions={[10, 25, 50, 100]}
                      startIndex={policiesStartIndex}
                      endIndex={policiesEndIndex}
                      canGoPrev={policiesCanGoPrev}
                      canGoNext={policiesCanGoNext}
                      onPageChange={policiesGoToPage}
                      onPageSizeChange={policiesSetPageSize}
                      onFirstPage={policiesFirstPage}
                      onLastPage={policiesLastPage}
                      onPrevPage={policiesPrevPage}
                      onNextPage={policiesNextPage}
                    />
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="roles" className="mt-6 space-y-6">
            {/* Controls bar */}
            <div className="flex flex-wrap items-center gap-3">
              <SearchInput
                value={roleSearch}
                onChange={(e) => setRoleSearch(e.target.value)}
                onClear={() => setRoleSearch("")}
                placeholder="Search by role name..."
                containerClassName="flex-1 min-w-[200px]"
              />
              <Button size="sm" disabled>
                <Plus className="mr-2 h-4 w-4" />
                Assign Role
              </Button>
            </div>

            {/* Table */}
            {rolesLoading && roles.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : roles.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No roles found</h3>
                <p className="text-muted-foreground">
                  {roleSearch ? "Try a different search term." : "No roles have been configured."}
                </p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead
                        direction={getRoleSortDirection("name")}
                        onSort={() => handleRoleSort("name")}
                      >
                        Role Name
                      </SortableTableHead>
                      <SortableTableHead
                        direction={getRoleSortDirection("description")}
                        onSort={() => handleRoleSort("description")}
                      >
                        Description
                      </SortableTableHead>
                      <SortableTableHead direction={null} onSort={() => {}} disabled>
                        Permissions
                      </SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roles.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {role.name}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {role.description}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {role.permissions.length > 0
                            ? role.permissions.slice(0, 3).join(", ") +
                              (role.permissions.length > 3
                                ? ` +${role.permissions.length - 3} more`
                                : "")
                            : "No permissions"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {rolesPagination.totalCount > 0 && (
                  <div className="mt-4 border-t pt-4">
                    <Pagination
                      page={rolesPagination.page}
                      totalPages={rolesPagination.totalPages}
                      totalCount={rolesPagination.totalCount}
                      pageSize={rolesPagination.pageSize}
                      pageSizeOptions={[10, 25, 50, 100]}
                      startIndex={rolesStartIndex}
                      endIndex={rolesEndIndex}
                      canGoPrev={rolesCanGoPrev}
                      canGoNext={rolesCanGoNext}
                      onPageChange={rolesGoToPage}
                      onPageSizeChange={rolesSetPageSize}
                      onFirstPage={rolesFirstPage}
                      onLastPage={rolesLastPage}
                      onPrevPage={rolesPrevPage}
                      onNextPage={rolesNextPage}
                    />
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="tester" className="mt-6">
            <div className="text-center py-12">
              <TestTube2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Policy Tester</h3>
              <p className="text-muted-foreground">
                Test if a subject has permission to perform an action on an object.
              </p>
              <p className="text-sm text-muted-foreground mt-2">Coming soon</p>
            </div>
          </TabsContent>

          <TabsContent value="statistics" className="mt-6 space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Policies</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{policiesPagination.totalCount}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Roles</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{rolesPagination.totalCount}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Policies on Page</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{policies.length}</div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="import-export" className="mt-6">
            <div className="text-center py-12">
              <Import className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Import/Export</h3>
              <p className="text-muted-foreground mb-4">
                Import or export policies and roles for backup or migration.
              </p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" disabled>
                  <Import className="mr-2 h-4 w-4" />
                  Import
                </Button>
                <Button variant="outline" disabled>
                  Export
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
